import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, MoreVertical, Trash2, WalletIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { AddCard } from "./AddCard";
import { useState } from "react";
import { toast } from "sonner";
import React from "react";
import {
  useDetachPaymentMethodMutation,
  useGetPaymentMethodsQuery,
  useSetDefaultPaymentMethodMutation,
} from "@/store/rest_api";
import { formatCardBrand } from "@/util/payment";

export function PaymentMethods() {
  const { data: paymentMethods, isLoading } = useGetPaymentMethodsQuery();
  const [detachPaymentMethod] = useDetachPaymentMethodMutation();
  const [setDefaultPaymentMethod, { isLoading: isSettingDefault }] = useSetDefaultPaymentMethodMutation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [cardToRemove, setCardToRemove] = useState<string | null>(null);

  const handleRemoveCard = async (paymentMethodId: string) => {
    try {
      await detachPaymentMethod(paymentMethodId).unwrap();
      toast.success("Payment method has been removed successfully");
    } catch (error: any) {
      toast.error(error?.detail || "Failed to remove card");
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      await setDefaultPaymentMethod(paymentMethodId).unwrap();
      toast.success("Default payment method updated");
    } catch (error: any) {
      toast.error(error?.detail || "Failed to update default card");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Payment Methods</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const hasCards = paymentMethods && paymentMethods.length > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <WalletIcon className="h-5 w-5" />
              Payment Methods
            </CardTitle>
            <CardDescription>{paymentMethods?.length || 0} card(s) on file</CardDescription>
          </div>
          {hasCards && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button>Add Card</Button>
              </DialogTrigger>
              <DialogContent>
                <AddCard onSuccess={() => setIsDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!hasCards ? (
          <div className="text-center py-8">
            <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-4">No payment methods added</p>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button>Add Your First Card</Button>
              </DialogTrigger>
              <DialogContent>
                <AddCard onSuccess={() => setIsDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <div className="space-y-2">
            {paymentMethods.map((pm) => (
              <div key={pm.id} className="flex items-center justify-between rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {formatCardBrand(pm.card?.brand)} •••• {pm.card?.last4}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Expires {pm.card?.expMonth}/{pm.card?.expYear}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {pm.isDefault ? (
                    <Badge variant="outline" className="text-xs">
                      Default
                    </Badge>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isSettingDefault}
                      onClick={() => handleSetDefault(pm.id)}
                    >
                      Set as default
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" title="Options">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="text-destructive" onClick={() => setCardToRemove(pm.id)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove card
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <AlertDialog
                    open={cardToRemove === pm.id}
                    onOpenChange={(open) => {
                      if (!open) {
                        setCardToRemove(null);
                      }
                    }}
                  >
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove payment method?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove the card ending in {pm.card?.last4} from your account. This action cannot be
                          undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleRemoveCard(pm.id)}>Remove</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
